<template>
    <div class="patient-allergies-container">
        <!-- Header con búsqueda y botón crear (oculto si showHeader es false) -->
        <div v-if="showHeader" class="allergies-header">
            <div class="header-title">
                <div class="icon-wrapper">
                    <i class="fas fa-triangle-exclamation"></i>
                </div>
                <div class="title-content">
                    <h3>Alergias del Paciente</h3>
                    <span v-if="allergiesCount > 0" class="subtitle">{{ allergiesCount }} {{ allergiesCount === 1 ?
                        'alergia registrada' : 'alergias registradas' }}</span>
                </div>
            </div>

            <div class="header-actions">
                <SearchFilterClose
v-model="searchQuery" placeholder="Buscar alergias..."
                    @update:model-value="handleSearch" />

                <button class="btn-create-allergy" @click="openCreateModal">
                    <i class="fas fa-plus"></i>
                    <span>Nueva Alergia</span>
                </button>
            </div>
        </div>

        <!-- Header simplificado para panel -->
        <div v-else class="allergies-header-simple">
            <div class="header-actions-simple">
                <SearchFilterClose
v-model="searchQuery" placeholder="Buscar alergias..."
                    @update:model-value="handleSearch" />

                <button class="btn-create-allergy" @click="openCreateModal">
                    <i class="fas fa-plus"></i>
                    <span>Nueva</span>
                </button>
            </div>
        </div>

        <!-- Lista de alergias -->
        <div v-if="loading && allergies.length === 0" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Cargando alergias...</p>
        </div>

        <div v-else-if="error" class="error-state">
            <i class="fas fa-exclamation-circle"></i>
            <p>{{ error }}</p>
            <button class="btn-retry" @click="loadAllergies">Reintentar</button>
        </div>

        <div v-else-if="!hasAllergies" class="empty-state">
            <div class="empty-icon-container">
                <div class="empty-icon-bg"></div>
                <i class="fas fa-shield-virus"></i>
            </div>
            <h4>No hay alergias registradas</h4>
            <p>Mantén un registro completo de las alergias del paciente<br>para garantizar un tratamiento seguro y
                efectivo.</p>
            <button class="btn-create-first" @click="openCreateModal">
                <i class="fas fa-plus"></i>
                <span>Registrar Primera Alergia</span>
            </button>
        </div>

        <div v-else class="allergies-list">
            <div
v-for="allergy in allergies" :key="allergy.id" class="allergy-card"
                :class="getSeverityClass(allergy.severity)" @click="viewAllergy(allergy)">
                <div class="allergy-icon" :class="getSeverityClass(allergy.severity)">
                    <i :class="getSeverityIcon(allergy.severity)"></i>
                </div>

                <div class="allergy-content">
                    <div class="allergy-header">
                        <h4 class="allergy-allergen">{{ allergy.allergen }}</h4>
                        <span class="severity-badge" :class="getSeverityClass(allergy.severity)">
                            {{ allergy.severity }}
                        </span>
                    </div>

                    <div class="allergy-details">
                        <div class="detail-item">
                            <i class="fas fa-tag"></i>
                            <span>{{ allergy.allergy_type }}</span>
                        </div>
                        <div class="detail-item">
                            <i class="fas fa-heartbeat"></i>
                            <span>{{ allergy.reaction_type }}</span>
                        </div>
                    </div>

                    <div class="allergy-meta">
                        <span class="allergy-date">
                            <i class="far fa-clock"></i>
                            {{ formatDate(allergy.created_at) }}
                        </span>
                    </div>
                </div>

                <div class="allergy-actions" @click.stop>
                    <button class="btn-icon" title="Editar" @click="openEditModal(allergy)">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-icon btn-danger" title="Eliminar" @click="confirmDelete(allergy)">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Paginación -->
        <div v-if="pagination.last_page > 1" class="pagination">
            <button
class="btn-page" :disabled="pagination.current_page === 1"
                @click="changePage(pagination.current_page - 1)">
                <i class="fas fa-chevron-left"></i>
            </button>

            <span class="page-info">
                Página {{ pagination.current_page }} de {{ pagination.last_page }}
            </span>

            <button
class="btn-page" :disabled="pagination.current_page === pagination.last_page"
                @click="changePage(pagination.current_page + 1)">
                <i class="fas fa-chevron-right"></i>
            </button>
        </div>

        <!-- Modal Crear/Editar -->
        <BaseModal
v-model="showModal" :title="isEditing ? 'Editar Alergia' : 'Nueva Alergia'"
            icon="fas fa-triangle-exclamation" :confirm-text="isEditing ? 'Actualizar' : 'Guardar'" :loading="loading"
            @confirm="saveAllergy" @close="closeModal">
            <div class="form-row">
                <div class="form-group">
                    <label for="allergy-allergen">
                        Alérgeno <span class="required">*</span>
                    </label>
                    <input
id="allergy-allergen" v-model="formData.allergen" type="text" class="form-control"
                        placeholder="Ej: Penicilina, Polen, Mariscos..." maxlength="255"
                        :class="{ 'is-invalid': formErrors.allergen }" />
                    <span v-if="formErrors.allergen" class="error-message">
                        {{ formErrors.allergen }}
                    </span>
                </div>

                <div class="form-group">
                    <label for="allergy-type">
                        Tipo de Alergia <span class="required">*</span>
                    </label>
                    <ChoicesSelect v-model="formData.allergy_type" :options="allergyTypeOptions" />
                    <span v-if="formErrors.allergy_type" class="error-message">
                        {{ formErrors.allergy_type }}
                    </span>
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label for="allergy-severity">
                        Severidad <span class="required">*</span>
                    </label>
                    <ChoicesSelect v-model="formData.severity" :options="severityOptions" />
                    <span v-if="formErrors.severity" class="error-message">
                        {{ formErrors.severity }}
                    </span>
                </div>

                <div class="form-group">
                    <label for="allergy-reaction">
                        Tipo de Reacción <span class="required">*</span>
                    </label>
                    <input
id="allergy-reaction" v-model="formData.reaction_type" type="text" class="form-control"
                        placeholder="Ej: Anafilaxia, Urticaria, Dificultad respiratoria..." maxlength="255"
                        :class="{ 'is-invalid': formErrors.reaction_type }" />
                    <span v-if="formErrors.reaction_type" class="error-message">
                        {{ formErrors.reaction_type }}
                    </span>
                </div>
            </div>
        </BaseModal>

        <!-- Modal Confirmar Eliminación -->
        <BaseModal
v-model="showDeleteModal" title="Confirmar Eliminación" icon="fas fa-exclamation-triangle"
            confirm-text="Eliminar" confirm-icon="fas fa-trash" modal-class="modal-confirm" :loading="loading"
            @confirm="handleDelete" @close="closeDeleteModal">
            <template #default>
                <p class="warning-text">¿Estás seguro de eliminar esta alergia?</p>
                <div class="allergy-preview" :class="getSeverityClass(allergyToDelete?.severity)">
                    <div class="preview-header">
                        <strong>{{ allergyToDelete?.allergen }}</strong>
                        <span class="severity-badge" :class="getSeverityClass(allergyToDelete?.severity)">
                            {{ allergyToDelete?.severity }}
                        </span>
                    </div>
                    <div class="preview-details">
                        <p><i class="fas fa-tag"></i> {{ allergyToDelete?.allergy_type }}</p>
                        <p><i class="fas fa-heartbeat"></i> {{ allergyToDelete?.reaction_type }}</p>
                    </div>
                </div>
                <p class="text-muted">Esta acción se puede revertir posteriormente.</p>
            </template>

            <template #footer>
                <button class="btn-secondary" @click="closeDeleteModal">
                    Cancelar
                </button>
                <button class="btn-danger" :disabled="loading" @click="handleDelete">
                    <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-trash"></i>
                    Eliminar
                </button>
            </template>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { usePatientAllergies } from '@/composables/usePatientAllergies';
import SearchFilterClose from '@/components/SearchFilterClose.vue';
import ChoicesSelect from '@/components/ChoicesSelect.vue';
import BaseModal from '@/components/BaseModal.vue';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const props = defineProps({
    patientId: {
        type: Number,
        required: true
    },
    showHeader: {
        type: Boolean,
        default: false
    }
});

const {
    allergies,
    loading,
    error,
    pagination,
    hasAllergies,
    allergiesCount,
    fetchAllergiesByPatient,
    createAllergy,
    updateAllergy,
    deleteAllergy
} = usePatientAllergies();

// State
const searchQuery = ref('');
const showModal = ref(false);
const showDeleteModal = ref(false);
const isEditing = ref(false);
const allergyToDelete = ref(null);
const formData = ref({
    allergen: '',
    allergy_type: '',
    severity: '',
    reaction_type: ''
});
const formErrors = ref({});

// Opciones para ChoicesSelect
const allergyTypeOptions = [
    { id: 'Medicamento', text: 'Medicamento' },
    { id: 'Alimento', text: 'Alimento' },
    { id: 'Ambiental', text: 'Ambiental' },
    { id: 'Insecto', text: 'Insecto' },
    { id: 'Contacto', text: 'Contacto' },
    { id: 'Otro', text: 'Otro' }
];

const severityOptions = [
    { id: 'Leve', text: 'Leve' },
    { id: 'Moderada', text: 'Moderada' },
    { id: 'Severa', text: 'Severa' }
];

// Methods
const loadAllergies = async (page = 1) => {
    try {
        await fetchAllergiesByPatient(props.patientId, {
            page,
            search: searchQuery.value,
            per_page: 10,
            sort_column: 'created_at',
            sort_order: 'desc'
        });
    } catch (err) {
        console.error('Error loading allergies:', err);
    }
};

const handleSearch = () => {
    loadAllergies(1);
};

const changePage = (page) => {
    loadAllergies(page);
};

const openCreateModal = () => {
    isEditing.value = false;
    formData.value = {
        allergen: '',
        allergy_type: '',
        severity: '',
        reaction_type: ''
    };
    formErrors.value = {};
    showModal.value = true;
};

const openEditModal = (allergy) => {
    isEditing.value = true;
    formData.value = {
        id: allergy.id,
        allergen: allergy.allergen,
        allergy_type: allergy.allergy_type,
        severity: allergy.severity,
        reaction_type: allergy.reaction_type
    };
    formErrors.value = {};
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    formData.value = {
        allergen: '',
        allergy_type: '',
        severity: '',
        reaction_type: ''
    };
    formErrors.value = {};
};

const validateForm = () => {
    formErrors.value = {};
    let isValid = true;

    if (!formData.value.allergen || formData.value.allergen.trim() === '') {
        formErrors.value.allergen = 'El alérgeno es requerido';
        isValid = false;
    }

    if (!formData.value.allergy_type || formData.value.allergy_type.trim() === '') {
        formErrors.value.allergy_type = 'El tipo de alergia es requerido';
        isValid = false;
    }

    if (!formData.value.severity || formData.value.severity.trim() === '') {
        formErrors.value.severity = 'La severidad es requerida';
        isValid = false;
    }

    if (!formData.value.reaction_type || formData.value.reaction_type.trim() === '') {
        formErrors.value.reaction_type = 'El tipo de reacción es requerido';
        isValid = false;
    }

    return isValid;
};

const saveAllergy = async () => {
    if (!validateForm()) {
        return;
    }

    try {
        if (isEditing.value) {
            await updateAllergy(formData.value.id, {
                allergen: formData.value.allergen,
                allergy_type: formData.value.allergy_type,
                severity: formData.value.severity,
                reaction_type: formData.value.reaction_type
            });
        } else {
            await createAllergy({
                patient_id: props.patientId,
                allergen: formData.value.allergen,
                allergy_type: formData.value.allergy_type,
                severity: formData.value.severity,
                reaction_type: formData.value.reaction_type
            });
        }

        closeModal();
        loadAllergies(pagination.value.current_page);
    } catch (err) {
        console.error('Error saving allergy:', err);
    }
};

const viewAllergy = (allergy) => {
    openEditModal(allergy);
};

const confirmDelete = (allergy) => {
    allergyToDelete.value = allergy;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    allergyToDelete.value = null;
};

const handleDelete = async () => {
    try {
        await deleteAllergy(allergyToDelete.value.id);
        closeDeleteModal();
        loadAllergies(pagination.value.current_page);
    } catch (err) {
        console.error('Error deleting allergy:', err);
    }
};

const getSeverityClass = (severity) => {
    const severityLower = severity?.toLowerCase() || '';
    if (severityLower === 'severa') return 'severity-high';
    if (severityLower === 'moderada') return 'severity-medium';
    return 'severity-low';
};

const getSeverityIcon = (severity) => {
    const severityLower = severity?.toLowerCase() || '';
    if (severityLower === 'severa') return 'fas fa-exclamation-triangle';
    if (severityLower === 'moderada') return 'fas fa-exclamation-circle';
    return 'fas fa-info-circle';
};

const formatDate = (date) => {
    if (!date) return '';
    return format(new Date(date), "d 'de' MMMM 'de' yyyy", { locale: es });
};

// Lifecycle
onMounted(() => {
    loadAllergies();
});

// Watchers
watch(() => props.patientId, () => {
    loadAllergies();
});
</script>

<style scoped>
.patient-allergies-container {
    padding: 0;
    background: transparent;
    min-height: 500px;
}

/* ========================================
   HEADER MODERNO
======================================== */
.allergies-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid #e2e8f0;
    flex-wrap: wrap;
    gap: 1.5rem;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.icon-wrapper {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.icon-wrapper i {
    color: white;
    font-size: 1.5rem;
}

.title-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.title-content h3 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
}

.subtitle {
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
}

.allergies-header-simple {
    margin-bottom: 1.5rem;
}

.header-actions,
.header-actions-simple {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1rem;
    align-items: center;
}

.btn-create-allergy {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    white-space: nowrap;
}

.btn-create-allergy:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

.btn-create-allergy:active {
    transform: translateY(0);
}

.btn-create-allergy i {
    font-size: 1rem;
}

/* ========================================
   ESTADOS (Loading, Error, Empty)
======================================== */
.loading-state,
.error-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
}

.loading-state i {
    font-size: 3.5rem;
    color: #ef4444;
    margin-bottom: 1.5rem;
}

.error-state i {
    font-size: 3.5rem;
    color: #ef4444;
    margin-bottom: 1.5rem;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 6rem 2rem;
    text-align: center;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border-radius: 20px;
    border: 2px dashed #fecaca;
    margin: 2rem 0;
}

.empty-icon-container {
    position: relative;
    margin-bottom: 2rem;
}

.empty-icon-bg {
    position: absolute;
    width: 140px;
    height: 140px;
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.6;
    }

    50% {
        transform: translate(-50%, -50%) scale(1.1);
        opacity: 0.8;
    }
}

.empty-state i {
    font-size: 4.5rem;
    color: #ef4444;
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 4px 8px rgba(239, 68, 68, 0.2));
}

.empty-state h4 {
    margin: 0.5rem 0;
    color: #1e293b;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
}

.empty-state p {
    color: #64748b;
    margin-bottom: 2.5rem;
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
}

.btn-retry,
.btn-create-first {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: none;
    border-radius: 14px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
}

.btn-retry:hover,
.btn-create-first:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(239, 68, 68, 0.45);
}

.btn-create-first i {
    font-size: 1.125rem;
    color: white;
}

/* ========================================
   LISTA DE ALERGIAS MODERNA
======================================== */
.allergies-list {
    display: grid;
    gap: 1rem;
}

.allergy-card {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    background: white;
    border-radius: 16px;
    border: 2px solid #f1f5f9;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.allergy-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.allergy-card.severity-high::before {
    background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
}

.allergy-card.severity-medium::before {
    background: linear-gradient(180deg, #f59e0b 0%, #d97706 100%);
}

.allergy-card.severity-low::before {
    background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
}

.allergy-card:hover {
    box-shadow: 0 8px 24px rgba(239, 68, 68, 0.15);
    transform: translateY(-2px);
}

.allergy-card:hover::before {
    opacity: 1;
}

.allergy-card.severity-high:hover {
    border-color: #ef4444;
}

.allergy-card.severity-medium:hover {
    border-color: #f59e0b;
}

.allergy-card.severity-low:hover {
    border-color: #3b82f6;
}

.allergy-icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    position: relative;
}

.allergy-icon.severity-high {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.allergy-icon.severity-medium {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.allergy-icon.severity-low {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.allergy-icon i {
    font-size: 1.75rem;
    position: relative;
    z-index: 1;
}

.allergy-icon.severity-high i {
    color: #dc2626;
}

.allergy-icon.severity-medium i {
    color: #d97706;
}

.allergy-icon.severity-low i {
    color: #2563eb;
}

.allergy-content {
    flex: 1;
    min-width: 0;
}

.allergy-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.allergy-allergen {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.01em;
    line-height: 1.4;
}

.severity-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.severity-badge.severity-high {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
}

.severity-badge.severity-medium {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
}

.severity-badge.severity-low {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
}

.allergy-details {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 0.875rem;
    flex-wrap: wrap;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.9375rem;
    font-weight: 500;
}

.detail-item i {
    font-size: 0.875rem;
    color: #94a3b8;
}

.allergy-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.allergy-date {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #94a3b8;
    font-weight: 500;
}

.allergy-date i {
    font-size: 0.875rem;
}

.allergy-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.btn-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-icon:hover {
    background: #667eea;
    border-color: #667eea;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-icon.btn-danger:hover {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border-color: #ef4444;
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* ========================================
   ESTILOS DEL FORMULARIO
======================================== */
.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-group label {
    font-weight: 600;
    color: #334155;
    font-size: 0.9375rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.required {
    color: #ef4444;
}

.form-group input,
.form-group select,
.form-group textarea {
    padding: 0.875rem 1.125rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.9375rem;
    color: #1e293b;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: #f8fafc;
    font-family: inherit;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
    outline: none;
    border-color: #ef4444;
    background: white;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.form-group.error input,
.form-group.error select,
.form-group.error textarea {
    border-color: #ef4444;
    background: #fef2f2;
}

.error-message {
    color: #ef4444;
    font-size: 0.875rem;
    font-weight: 500;
}

.btn-secondary,
.btn-primary,
.btn-danger {
    padding: 0.875rem 1.75rem;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: none;
}

.btn-secondary {
    background: white;
    color: #64748b;
    border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
}

.btn-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-danger:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

/* ========================================
   ESTILOS DE PREVIEW
======================================== */
.warning-text {
    text-align: center;
    color: #64748b;
    margin-bottom: 1.5rem;
    font-size: 1rem;
}

.text-muted {
    text-align: center;
    color: #94a3b8;
    font-size: 0.875rem;
}

/* ========================================
   ESTILOS ESPECÍFICOS DE ALERGIAS
======================================== */
.allergy-preview {
    background: linear-gradient(135deg, #fef2f2 0%, #ffffff 100%);
    padding: 1.5rem;
    border-radius: 16px;
    border: 2px solid #fee2e2;
    margin-bottom: 2rem;
}

.allergy-preview h4 {
    margin: 0 0 1rem;
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.preview-details {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.preview-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #64748b;
    font-size: 0.9375rem;
}

.preview-item i {
    width: 20px;
    text-align: center;
    color: #94a3b8;
}

.preview-item strong {
    color: #334155;
    font-weight: 600;
}

/* ========================================
   RESPONSIVE
======================================== */
@media (max-width: 768px) {
    .allergies-header {
        flex-direction: column;
        align-items: stretch;
    }

    .header-actions,
    .header-actions-simple {
        width: 100%;
    }

    .btn-create-allergy {
        width: 100%;
        justify-content: center;
    }

    .form-row {
        grid-template-columns: 1fr;
    }

    .allergy-card {
        flex-direction: column;
    }

    .allergy-actions {
        width: 100%;
        justify-content: flex-end;
    }

    .modal-content {
        max-width: 100%;
        border-radius: 16px;
    }

    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 1.5rem;
    }
}

@media (max-width: 480px) {
    .icon-wrapper {
        width: 48px;
        height: 48px;
    }

    .icon-wrapper i {
        font-size: 1.25rem;
    }

    .title-content h3 {
        font-size: 1.25rem;
    }

    .allergy-icon {
        width: 48px;
        height: 48px;
    }

    .allergy-icon i {
        font-size: 1.5rem;
    }

    .allergy-allergen {
        font-size: 1rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .btn-secondary,
    .btn-primary,
    .btn-danger {
        width: 100%;
        justify-content: center;
    }
}
</style>
